// import * as THREE from 'three';
import axios from "axios";
import { atom, useAtom } from "jotai";
import { isEqual } from "lodash";
import cloneDeep from "lodash.clonedeep";
import { traverse } from "object-traversal";
import { useEffect, useRef, useState } from "react";
import sift from "sift";
import toastr from "toastr";
import EmbedController from "../../../embed/EmbedController";
import AlertSlackOfError from "../../../monitoring/AlertSlackOfError";
import { pushGTMAnalyticsEvent } from "../../modules/pushGTMAnalyticsEvent";
import refreshExperience from "../../modules/refreshExperience";

// default data is init here
// will be overridden if data is being passed in from shopify
let shopifyData = require("./defaultShopifyData.json");

/**
 *
 * products
 *
 *
 */

// THIS IS THE GLOBAL products DATA OBJECT
export const products_state = atom({
  activeId: null,
  activeObj: null,
  array: null,
  isPrimed: false,
});

export const getDefaultProductFromQueryParam = () => {
  if (EmbedController.isEmbedded) return null;

  try {
    return new URL(window.location.href).searchParams.get("defaultProduct");
  } catch {
    return null;
  }
};

export const defaultProductId = () => {
  return getDefaultProductFromQueryParam() || window._tt?.shopifyData?.product?.tt_product_id || shopifyData.product.tt_product_id;
};

// function to update products_state (to be used around the app)
// when this gets updated, GlobalDataManagers will inform the URL to update accordingly
export const update_products_activeId = atom(null);

// holds the fetched product array
const all_products_array = atom(null);

// holds active accordion
export const accordion_state = atom(null);

/**
 *
 * components
 *
 */

// THIS IS THE GLOBAL components DATA OBJECT
export const components_state = atom({
  activeId: null,
  activeObj: null,
  array: null,
  isPrimed: false,
});

export const defaultComponentId = () => "top-material";

// function to update components_state (to be used around the app)
// when this gets updated, GlobalDataManagers will inform the URL to update accordingly
export const update_components_activeId = atom(null);

/**
 *
 * items
 *
 */

// THIS IS THE GLOBAL items DATA OBJECT
export const items_state = atom({
  activeIds: null,
  activeObjs: null,
  array: null,
  isPrimed: false,
});

// function to update items_state (to be used around the app)
// when this gets updated, GlobalDataManagers will inform the URL to update accordingly
export const update_items_activeIds = atom(null);

// holds the fetched non_items array
const non_items_array = atom(null);

/**
 *
 *
 * MISC
 *
 *
 */

export const is_experience_loaded_and_revealed = atom(false);

export const model_scale = atom(1);
export const camera_starting_position = atom([0, 0.85, -2]);
export const camera_lookAt = atom([0, 0.5, 0]);
export const model_starting_position = atom([0, 0.4, 0]);
export const model_starting_rotation = atom([-Math.PI, Math.PI * -0.15, 0]);
export const model_rotation_override = atom();

// Used when we want loading screen for assets not loading by LoadingManager
export const loading_state = atom(false);
export const loading_count = atom(0);
export const update_loading_count = atom(null, (get, set, _arg) => set(loading_count, get(loading_count) + _arg));

// used to store a base64 image of the canvas to send to shopping cart
export const canvas_base64 = atom(null);

// create unique session id for this runtime instance that can be used for us to identify errors
const { v4: uuidv4 } = require("uuid");
const sessionId = uuidv4();
export const session_id = atom(sessionId);

// used to store the dynamically created custom textures
const textureAtom_sidePatches = atom(null);
const textureAtom_frontPatch = atom(null);
export const customTextureAtomsObj = {
  textureAtom_sidePatches: textureAtom_sidePatches,
  textureAtom_frontPatch: textureAtom_frontPatch,
};

// data passed in from the UrlDataController that determines activeId's
export function GlobalDataManagers({
  // products
  products_activeId_fromURL,
  update_products_activeId_inURL,
  // components
  components_activeId_fromURL,
  update_components_activeId_inURL,
  // items
  items_activeIds_fromURL,
  update_items_activeIds_inURL,
  // components and items
  update_component_and_items_inURL,
}) {
  /**
   *
   * Load the data required to initialize the experience
   *
   */

  const [isInitDataLoaded, setIsInitDataLoaded] = useState(false);
  useEffect(() => {
    loadInitData();
  }, []);

  async function loadInitData() {
    // Wait for embed registration to complete before checking shopify data (to prevent default product data from being used)
    if (EmbedController?.isEmbedded) {
      let attempts = 0;
      while (!window._tt?.shopifyData && attempts < 20) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }
      if (!window._tt?.shopifyData) {
        AlertSlackOfError("GlobalDataManagers", "window._tt.shopifyData not available after 2s wait");
      }
    }

    // override default shopifyData if it's being passed in
    if (window._tt?.shopifyData) shopifyData = window._tt.shopifyData;

    const defaultProductFromQuery = getDefaultProductFromQueryParam();
    if (defaultProductFromQuery) {
      shopifyData = {
        ...shopifyData,
        product: {
          ...shopifyData.product,
          tt_product_id: defaultProductFromQuery,
        },
      };
    }

    await Promise.all([loadItemsListData(), loadItemTemplateData()]);

    await Promise.all([
      loadProductData(),
      loadComponentsData(),
      // loadSectionsData(),
      loadItemsData(),
      //// loadNonItemsData(),
    ]);

    compileSectionsAndItemsLists();

    setIsInitDataLoaded(true);
  }

  /**
   *
   * products
   *
   *
   */

  const [productsState, setProductsState] = useAtom(products_state);
  const [requested_products_activeId, reset_update_products_activeId] = useAtom(update_products_activeId);
  const [allProductsArray, setAllProductsArray] = useAtom(all_products_array);
  const allProductsArray_ref = useRef();

  // load products for experience to start
  async function loadProductData() {
    const res = await axios("/data/products.json");
    let allProducts = res.data;
    allProducts = updateProductDataFromShopifyData(allProducts);
    allProductsArray_ref.current = allProducts;
    setAllProductsArray(allProducts);
  }

  function updateProductDataFromShopifyData(allProductsArray) {
    // get matching product from array
    allProductsArray.forEach((product) => {
      product.shopify = { ...shopifyData.product };
      if (product.shopify.defaultConfigObj) {
        product.defaultItems = {
          ...product.defaultItems,
          ...product.shopify.defaultConfigObj,
        };
      }
    });

    return allProductsArray;
  }

  // when some child tells us to update products_state.activeId, we tell the URL to update accordingly
  useEffect(() => {
    if (requested_products_activeId) {
      update_products_activeId_inURL(requested_products_activeId);
      reset_update_products_activeId(null); // reset this so it'll always register as an update
    }
  }, [requested_products_activeId]);

  // when URL tells us there is a new active product, update products_state
  useEffect(() => {
    if (!isInitDataLoaded || !products_activeId_fromURL) return;

    // Check if we need to migrate an old product ID to a new one
    let migratedProductId = migrateOldProductId(products_activeId_fromURL);
    if (migratedProductId !== products_activeId_fromURL) {
      // Update URL with new product ID and preserve all other parameters
      update_products_activeId_inURL(migratedProductId);
      return; // Exit early, the URL update will trigger this useEffect again with the new ID
    }

    let productsArray = filterProductData();
    let activeProduct = productsArray.find((productObj) => {
      return productObj._id === products_activeId_fromURL;
    });

    // Check if the active product id doesn't match the shopify product id
    // this happens when user changes bike models using the modal
    if (activeProduct._id !== activeProduct.shopify.tt_product_id) {
      console.log("Active product id doesn't match shopify product id", activeProduct._id, activeProduct.shopify.tt_product_id);
      reset_update_products_activeId(activeProduct.shopify.tt_product_id);
      return;
    }
    let newProductsState = {
      activeId: products_activeId_fromURL,
      activeObj: activeProduct,
      array: productsArray,
      isPrimed: true,
    };
    setProductsState(newProductsState);
    console.log(`____ NEW productsState ______`, newProductsState);
    pushGTMAnalyticsEvent({
      event: "pageview",
      page: { path: `/virtualPageView/${newProductsState.activeId}`, title: `Virtual Title - ${newProductsState.activeObj.displayName}` },
    });
  }, [products_activeId_fromURL, isInitDataLoaded]);

  // // FALLBACK: Re-check shopify data override after component mount (embed should be ready)
  // useEffect(() => {
  //   if (EmbedController.isEmbedded && window._tt?.shopifyData && shopifyData?.product?.displayName === "Demo Bike Model") {
  //     console.log("LATE OVERRIDE: Applying shopify data after mount");
  //     shopifyData = window._tt.shopifyData;

  //     // Reload product data with correct shopify data
  //     loadProductData();
  //   }
  // }, [isInitDataLoaded]);

  // filter and return an array with the active product
  function filterProductData() {
    let applicableProducts = allProductsArray.filter(sift({ _id: { $in: [products_activeId_fromURL] } }));
    return applicableProducts;
  }

  // CUSTOM CODE: handles migration from old product IDs to new product IDs for backward compatibility
  const PRODUCT_ID_MIGRATIONS = {
    "xtra-xtra-small-dirtbike-seat": "3-rib-seat",
    "xtra-small-dirtbike-seat": "4-rib-seat",
    "small-dirtbike-seat": "5-rib-seat",
    "full-dirtbike-seat": "6-rib-seat",
    "xtra-long-dirtbike-seat": "7-rib-seat",
  };
  function migrateOldProductId(oldProductId) {
    const newProductId = PRODUCT_ID_MIGRATIONS[oldProductId];
    if (newProductId) {
      console.log(`Migrating old product ID "${oldProductId}" to new product ID "${newProductId}"`);
      return newProductId;
    }
    return oldProductId; // Return original if no migration needed
  }

  /**
   *
   * components
   *    also, sections are nested inside components
   *    also, items/items-list are nested inside components
   *
   */

  const [componentsState, setComponentsState] = useAtom(components_state);
  const [requested_components_activeId, reset_update_components_activeId] = useAtom(update_components_activeId);
  const allComponentsArray_ref = useRef();
  const allSectionsArray_ref = useRef();
  const itemsListArrays_ref = useRef();

  // load components for experience to start
  async function loadComponentsData() {
    let res = await axios("/data/components.json");
    let allComponents = res.data;
    allComponents = updateComponentDataFromShopifyData(allComponents); // CUSTOM CODE
    allComponentsArray_ref.current = allComponents;
  }
  async function loadSectionsData() {
    let res = await axios("/data/sections.json");
    let allSections = res.data;
    allSectionsArray_ref.current = allSections;
  }
  function updateComponentDataFromShopifyData(allComponentsArray) {
    let _allComponentsArray = [...allComponentsArray];
    /**
     * Exclusions
     */
    _allComponentsArray = _allComponentsArray.map((componentObj) => {
      // Apply Shopify exclusions
      if (applyShopifyExclusions(componentObj) === true) {
        componentObj.excluded = true;
      }
      return componentObj;
    });
    return _allComponentsArray;
  }

  // inject correct sections objects and items-list id's into component objs
  function compileSectionsAndItemsLists() {
    let newComponentObj;
    let newComponentsArray = [];

    allComponentsArray_ref.current.forEach((componentObj) => {
      newComponentObj = { ...componentObj };

      // sections
      if (allSectionsArray_ref.current) {
        let sectionIds = newComponentObj.sections;
        sectionIds?.forEach((sectionId, index) => {
          newComponentObj.sections[index] = allSectionsArray_ref.current.find((sectionObj) => sectionObj._id === sectionId);
        });
      }

      // items-list
      let expandedItemsArray = [];
      newComponentObj.items.forEach((itemId, index) => {
        // expand any -items-list
        if (itemId?.includes("-items-list")) {
          let itemsArray_fromList = itemsListArrays_ref.current[itemId];
          // add the array of item id's from the items-list to the component.items
          expandedItemsArray = expandedItemsArray.concat(itemsArray_fromList);
        }
        // push the regular item id
        else {
          expandedItemsArray.push(itemId);
        }
      });

      // update component's .items
      newComponentObj.items = expandedItemsArray;

      // update component obj in originalArray
      newComponentsArray.push(newComponentObj);
    });

    allComponentsArray_ref.current = newComponentsArray;
  }

  // when some child tells us to update components_state.activeId, we tell the URL to update accordingly
  useEffect(() => {
    if (requested_components_activeId && requested_components_activeId != components_activeId_fromURL) {
      update_components_activeId_inURL(requested_components_activeId);
      reset_update_components_activeId(null); // reset this so it'll always register as an update
    }
  }, [requested_components_activeId]);

  // when active product changes, we tell the URL to update the active component to be the product's first component
  // unless active component is already set in URL (i.e. shopper returning to a saved config)
  useEffect(() => {
    if (!productsState.activeId || components_activeId_fromURL) return;
    let activeProduct = productsState.array.find((productObj) => productObj._id === productsState.activeId);
    if (activeProduct.components[0]) update_components_activeId_inURL(activeProduct.components[0]);
  }, [productsState.activeId]);

  // when URL tells us there is a new active component, update components_state
  useEffect(() => {
    if (!components_activeId_fromURL || !productsState.isPrimed) return;
    let componentChoicesArray = filterComponentData(items_activeIds_fromURL);
    let activeComponent = componentChoicesArray.find((componentObj) => componentObj._id === components_activeId_fromURL);
    let newComponentsState = { activeId: components_activeId_fromURL, activeObj: activeComponent, array: componentChoicesArray, isPrimed: true };
    setComponentsState(newComponentsState);
    console.log(`____ NEW componentsState ______`, newComponentsState);
  }, [
    components_activeId_fromURL,
    productsState.isPrimed,
    items_activeIds_fromURL, // CUSTOM CODE: since component's have item dependencies we need to update components when items are updated
  ]);

  // filter and return an array with the list of components
  function filterComponentData(items_activeIds_fromURL) {
    let applicableComponentIds = productsState.array.find((productObj) => productObj._id === productsState.activeId).components;
    let applicableComponents = allComponentsArray_ref.current.filter(sift({ _id: { $in: applicableComponentIds } }));
    // make applicableComponents have same order as product.components array
    applicableComponents.sort((a, b) => {
      return applicableComponentIds.indexOf(a._id) - applicableComponentIds.indexOf(b._id);
    });

    // apply any dependencies — fill missing keys (e.g. legacy URLs without side-logo)
    // from defaults before resolving, so componentsState matches itemsState normalization
    if (items_activeIds_fromURL) {
      const effectiveActiveIds = { ...defaultItemActiveIds(applicableComponents), ...items_activeIds_fromURL };
      let activeItemIdsArray = getActiveItemIdsArray(effectiveActiveIds);
      applicableComponents.forEach((component) => {
        updateObjWithDependencies(component, activeItemIdsArray);
      });
    }

    return applicableComponents;
  }

  function getDefaultItemFromComponent(componentId) {
    let itemId = allComponentsArray_ref.current?.find((componentObj) => componentObj._id === componentId)?.items[0];
    let itemObj = allItemsArray_ref.current?.find((item) => item._id === itemId);
    return itemObj;
  }

  // CUSTOM CODE: handles the edge case where an old item id is being used and needs to be swapped for a new one
  function getReplacementItem(componentId, oldItemId) {
    try {
      let newItemId;
      switch (oldItemId) {
        case "black__lacing_color":
          newItemId = "black_full__lacing_color";
          break;

        default:
          newItemId = getDefaultItemFromComponent(componentId)?._id;
          break;
      }
      let itemObj = allItemsArray_ref.current?.find((item) => item._id === newItemId);
      return itemObj;
    } catch (error) {
      const _error = {
        title: "ERROR in getReplacementItem",
        oldItemId,
        componentId,
        error,
        components: JSON.stringify(productsState.activeObj?.components),
      };
      throw JSON.stringify(_error);
    }
  }

  /**
   *
   * items / non_items
   *
   */

  const [itemsState, setItemsState] = useAtom(items_state);
  const [requested_items_activeIds, reset_update_items_activeIds] = useAtom(update_items_activeIds);
  const allItemsArray_ref = useRef();
  const [allNonItemsArray, setAllNonItemsArray] = useAtom(non_items_array);
  const itemsTemplates_ref = useRef();

  function defaultItemActiveIds(componentsArray = componentsState.array) {
    let activeIds = {};
    const activeProductDefaultItems = productsState.activeObj.defaultItems;
    componentsArray.forEach((componentObj) => {
      if (componentObj?.items[0]?.includes("items-list")) {
        console.log("componentsState.array", componentsState.array);
        console.log("allComponentsArray_ref.current", allComponentsArray_ref.current);
        console.log("componentObj.items", componentObj.items);
        AlertSlackOfError("defaultItemActiveIds in GDM", `items-list is still active in componentObj ${componentObj}`);
        refreshExperience("GlobalDataManagers", sessionId, false, true);
      }
      if (activeProductDefaultItems && activeProductDefaultItems[componentObj._id])
        activeIds[componentObj._id] = { _id: activeProductDefaultItems[componentObj._id] };
      else activeIds[componentObj._id] = { _id: componentObj.items[0] };

      if (shopifyData.product["omit-ribs"] === true && componentObj._id === "rib-type") {
        activeIds[componentObj._id] = { _id: "no-ribs" };
      }
      if (shopifyData.product["omit-front-patch"] === true && componentObj._id === "custom-patch-front") {
        activeIds[componentObj._id] = { _id: "no-front-patch" };
      }
      if (shopifyData.product["omit-side-patches"] === true && componentObj._id === "custom-patches-side") {
        activeIds[componentObj._id] = { _id: "no-side-patches" };
      }
      if (shopifyData.product["omit-side-patches"] === true && componentObj._id === "side-logo") {
        activeIds[componentObj._id] = { _id: "no-side-logos" };
      }
    });

    return activeIds;
  }

  // load items for experience to start
  async function loadItemsData() {
    let res = await axios("/data/items.json");
    let ttItems = res.data;
    let allItems = combineItemsAndApplyTemplates(ttItems, shopifyData.items);
    allItemsArray_ref.current = allItems;
  }
  // async function loadNonItemsData() {
  //   let res = await fetch('/data/non_items.json');
  //   let allNonItems = await res.json();
  //   setAllNonItemsArray(allNonItems);
  // }

  // load items-list for experience to start
  // CUSTOM CODE
  async function loadItemsListData() {
    itemsListArrays_ref.current = {};
    Object.entries(shopifyData.itemsLists).forEach(([listName, list]) => {
      itemsListArrays_ref.current[listName] = list;
    });
  }
  // CUSTOM CODE
  async function loadItemTemplateData() {
    let [color, gripper, pattern] = await Promise.all([
      axios("/data/item-templates/color.json").then((value) => value.data),
      axios("/data/item-templates/gripper.json").then((value) => value.data),
      axios("/data/item-templates/pattern.json").then((value) => value.data),
    ]);
    itemsTemplates_ref.current = {
      color,
      gripper,
      pattern,
    };
  }
  // CUSTOM CODE: use templates to expand items with default data
  function combineItemsAndApplyTemplates(ttItems, shopifyItems) {
    // apply prices from shopify to ttItems
    ttItems = ttItems.map((ttItem) => {
      let matchingShopifyItem = shopifyItems.find((shopifyItem) => shopifyItem._id === ttItem._id);
      if (!matchingShopifyItem) return ttItem;
      return {
        ...ttItem,
        ...matchingShopifyItem,
      };
    });
    // apply our tt templates to shopifyItems
    shopifyItems = shopifyItems
      .map((shopifyItem) => {
        if (ttItems.find((ttItem) => ttItem._id === shopifyItem._id)) return null; // check if it's already present in ttItems
        let template = determineTemplateFromItemId(shopifyItem._id);
        if (!template) return null; // if it's not a template item, return null
        return {
          ...template,
          ...shopifyItem,
          material_obj: {
            ...template.material_obj,
            properties: {
              ...template.material_obj.properties,
              map: shopifyItem.imageSrc,
            },
          },
        };
      })
      .filter((ttItemOrNull) => ttItemOrNull !== null); // removes nulls, which are items that are already in ttItems
    return [...ttItems, ...shopifyItems];
  }
  // CUSTOM CODE:
  function determineTemplateFromItemId(itemId) {
    if (itemId.includes("color-gripper")) return itemsTemplates_ref.current.gripper;
    else if (itemId.includes("color-stitching")) return itemsTemplates_ref.current.color;
    else if (itemId.includes("color-text")) return itemsTemplates_ref.current.color;
    else if (itemId.includes("-pattern")) return itemsTemplates_ref.current.pattern;
    else if (itemId.includes("-patch")) return itemsTemplates_ref.current.color;
    else if (itemId.includes("-logo")) return itemsTemplates_ref.current.color;
    else if (itemId === "hidden-rib") return itemsTemplates_ref.current.color;
  }

  // when active product changes & components_state is primed, we tell the URL to update the activeIds to each component's default item
  // happens on site load unless activeIds is already set in URL (i.e. shopper returning to a saved config)
  useEffect(() => {
    if (!componentsState.isPrimed || items_activeIds_fromURL) return;
    update_items_activeIds_inURL(defaultItemActiveIds());
  }, [productsState.activeId, componentsState.isPrimed, componentsState]);

  // when some child tells us to update items_state.activeIds, we tell the URL to update accordingly
  useEffect(() => {
    if (requested_items_activeIds && (items_activeIds_fromURL || defaultItemActiveIds())) {
      const normalizedRequestedIds = normalizeLogoSelections(requested_items_activeIds);
      // CUSTOM CODE:
      // need to know what changed so we can change the active component id
      // _____________________________________________________________________
      let newActiveComponent = getObjectDiff(items_activeIds_fromURL || defaultItemActiveIds(), normalizedRequestedIds)[0];
      // side-logo is hidden (no accordion); keep URL focused on the Thrill Seekers Logo section
      if (newActiveComponent === "side-logo") {
        newActiveComponent = "patch-material-rear";
      }
      if (newActiveComponent) {
        update_component_and_items_inURL(newActiveComponent, normalizedRequestedIds);
        // _____________________________________________________________________
        // update_items_activeIds_inURL(requested_items_activeIds);
        reset_update_items_activeIds(null); // reset this so it'll always register as an update
      }
    }
  }, [requested_items_activeIds]);

  // when URL tells us there is a new items.activeIds, update items_state
  useEffect(() => {
    if (!items_activeIds_fromURL || !componentsState.isPrimed) return;
    let items_activeIds_clone = cloneDeep(items_activeIds_fromURL);
    items_activeIds_clone = checkForItemsLists(items_activeIds_clone); // CUSTOM CODE: handles sporadic error where some active id's were items-lists still
    // items_activeIds_clone = checkForOldComponentIds(items_activeIds_clone); // CUSTOM CODE: handles edge case where old id's are saved in URL
    items_activeIds_clone = addMissingComponents(items_activeIds_clone); // CUSTOM CODE: handles edge case where items activeIds from URL are missing some components
    items_activeIds_clone = updateOmittedItemsToDefault(items_activeIds_clone, componentsState); // CUSTOM CODE: handles edge case where configuration can be passed from one product to another and it overrides the omitted items
    items_activeIds_clone = normalizeLogoSelections(items_activeIds_clone);
    let newItemsArray = getUpdatedItemsArray(items_activeIds_clone);

    // #########################################################################################
    // CUSTOM CODE: handling the dependencies for the Khalifa Kush product
    let result = handleKhalifaKushDependencies(items_activeIds_clone, newItemsArray, components_activeId_fromURL);
    items_activeIds_clone = result.items_activeIds_clone;
    newItemsArray = result.newItemsArray;
    // #########################################################################################

    let newActiveObjs = getActiveItemObjs(items_activeIds_clone, newItemsArray);
    let newItemsState = {
      activeIds: items_activeIds_clone,
      activeObjs: newActiveObjs,
      array: newItemsArray,
      isPrimed: true,
    };
    setItemsState(newItemsState);
    console.log(`____ NEW itemsState ______`, newItemsState);
  }, [items_activeIds_fromURL, componentsState.isPrimed]);

  /**
   *
   *
   *
   */
  function normalizeLogoSelections(activeIds) {
    const newActiveIds = { ...activeIds };

    if (shopifyData.product["omit-side-patches"] === true) {
      newActiveIds["side-logo"] = { _id: "no-side-logos" };
    }

    const sideLogoId = newActiveIds["side-logo"]?._id;
    const topLogoId = newActiveIds["patch-material-rear"]?._id;

    // Guard invalid side-logo values (should only ever be yes/no)
    if (sideLogoId && sideLogoId !== "no-side-logos" && sideLogoId !== "yes-side-logos") {
      newActiveIds["side-logo"] = { _id: "yes-side-logos" };
    }

    // At least one logo must be active
    const topOff = topLogoId === "no-top-logo";
    const sideOff = !newActiveIds["side-logo"]?._id || newActiveIds["side-logo"]._id === "no-side-logos";

    if (topOff && sideOff) {
      newActiveIds["patch-material-rear"] = { _id: "white-color-logo" };
      newActiveIds["side-logo"] = { _id: "no-side-logos" };
    }

    return newActiveIds;
  }

  function handleKhalifaKushDependencies(activeIds, itemsArray, activeComponentId) {
    let newActiveIds = { ...activeIds };
    let newItemsArray = [...itemsArray];

    const isTopMaterialBeingChangedByUser = activeComponentId === "top-material";
    if (isTopMaterialBeingChangedByUser) {
      // Rule 1: Default the rear patch to the Khalifa Kush item if the Khalifa Kush pattern is chosen as the top material
      if (newActiveIds["top-material"]._id === "khalifa-kush-pattern") {
        newActiveIds["patch-material-rear"] = { _id: "khalifa-kush-color-logo" };
      }
      // Rule 2: Only display the Khalifa Kush color logo as an option if the Khalifa Kush pattern is chosen as the top material
      else if (newActiveIds["patch-material-rear"]._id === "khalifa-kush-color-logo") {
        newActiveIds["patch-material-rear"] = { _id: "white-color-logo" };
      }
    }

    return { items_activeIds_clone: newActiveIds, newItemsArray };
  }

  function checkForItemsLists(activeIds) {
    let newActiveIds = { ...activeIds };
    Object.keys(activeIds).forEach((componentId) => {
      if (activeIds[componentId]?._id?.includes("-items-list")) {
        let itemsArray_fromList = itemsListArrays_ref.current[activeIds[componentId]._id];
        newActiveIds[componentId]._id = productsState.activeObj.defaultItems[componentId] || itemsArray_fromList[0];
      }
    });
    return newActiveIds;
  }

  const omittedComponentToItemsMap = {
    "custom-patch-front": "no-front-patch",
    "custom-patches-side": "no-side-patches",
    "side-logo": "no-side-logos",
    "rib-type": "no-ribs",
    "tall-seat-cover": "no-tall-seat-cover",
    "double-side-wall": "no-double-side-wall",
    "comfort-foam": "no-comfort-foam",
  };

  function updateOmittedItemsToDefault(activeIds, componentsState) {
    let newActiveIds = { ...activeIds };

    componentsState.array.forEach((componentObj) => {
      if (componentObj.excluded && omittedComponentToItemsMap[componentObj._id]) {
        console.log(`____ updating ${componentObj._id} to ${omittedComponentToItemsMap[componentObj._id]} ______`);
        newActiveIds[componentObj._id] = { _id: omittedComponentToItemsMap[componentObj._id] };
      }
    });

    return newActiveIds;
  }

  // CUSTOM code
  // function checkForOldComponentIds(activeIds) {
  //   let newActiveIds = {...activeIds};
  //   Object.keys(activeIds).forEach((componentId) => {
  //     if (componentId == 'thumb_text__firstBase') {
  //       newActiveIds['thumb_customText__firstBase'] = {"_id": "no_customText_thumb"};
  //       newActiveIds['embroidery_customText_color'] = {"_id": "black__embroidery_color"};
  //       delete newActiveIds['thumb_text__firstBase'];
  //     }
  //     if (componentId == 'thumb_customText__singleWeltFielders') {
  //       newActiveIds['thumb_customText'] = newActiveIds['thumb_customText__singleWeltFielders'];
  //       delete newActiveIds['thumb_customText__singleWeltFielders'];
  //     }
  //     if (componentId == 'thumb_customText__firstBase') {
  //       newActiveIds['thumb_customText'] = newActiveIds['thumb_customText__firstBase'];
  //       delete newActiveIds['thumb_customText__firstBase'];
  //     }
  //   })
  //   return newActiveIds;
  // }

  function addMissingComponents(activeIds) {
    let newActiveIds = { ...defaultItemActiveIds(), ...activeIds };
    return newActiveIds;
  }

  function getObjectDiff(obj1, obj2) {
    if (!obj1 || !obj2) return [];
    const diff = Object.keys(obj1).reduce((result, key) => {
      if (!obj2.hasOwnProperty(key)) {
        result.push(key);
      } else if (isEqual(obj1[key], obj2[key])) {
        const resultKeyIndex = result.indexOf(key);
        result.splice(resultKeyIndex, 1);
      }
      return result;
    }, Object.keys(obj2));

    return diff;
  }

  function getActiveItemObjs(activeIds, itemsArray) {
    const unavailableItems = [];
    let activeObjs = { ...activeIds };
    let mods = {};

    const updateObjViaPaths = (objToUpdate, mods) => {
      for (var path in mods) {
        var k = objToUpdate;
        var steps = path.split(".");
        steps.pop(); // removing the _id entry so we replace whole object
        var last = steps.pop();
        steps.forEach((e) => (k[e] = k[e] || {}) && (k = k[e]));
        k[last] = mods[path];
        // if (!mods[path]?._id || !k[last]?._id) {
        //   console.log('path', path)
        // console.log('mods[path]', mods[path])
        // console.log('k[last]', k[last])
        // AlertSlackOfError('getActiveItemObjs in GDM', `Blank active item obj: ${path} | ${JSON.stringify(activeIds)}}`)
        // refreshExperience("GlobalDataManagers", sessionId, false, true);
        // }
      }
      return objToUpdate;
    };

    traverse(activeObjs, (context) => {
      let { key, value, meta } = context;
      // if we've found an abbreviated item obj
      if (key === "_id") {
        // add its path and the full item obj to mods
        mods[meta.currentPath] = { ...itemsArray.find((item) => item._id === value) };
        // check for edge case where old item id's are saved in URL
        const isEmpty = Object.keys(mods[meta.currentPath]).length === 0;
        if (isEmpty) {
          // AlertSlackOfError('getActiveItemObjs in GDM', `Blank active item obj: ${meta.currentPath} = ${value} being replaced properly`)
          mods[meta.currentPath] = getReplacementItem(meta.currentPath.split(".")[0], value);
          unavailableItems.push({
            componentId: meta.currentPath.split(".")[0],
            unavailableItemId: value,
            newItemId: mods[meta.currentPath]?._id,
          });
        }
      }
      // else if (key === "patch-material-rear" && Object.keys(value).length === 0) {
      //   activeIds[meta.currentPath] = { _id: "white-color-logo" };
      //   const defaultRearMaterial = shopifyData.items.find((item) => item._id === "white-color-logo")
      //   activeObjs[meta.currentPath] = defaultRearMaterial;
      // } else {
      //   console.info("***** NO KEY FOUND ******", key);
      // }
    });

    if (unavailableItems.length > 0) {
      // alert user of missing items
      unavailableItemAlerts([...new Set(unavailableItems.map((item) => item.unavailableItemId))]);
      // update active ids in URL
      let newActiveIds = { ...activeIds };
      unavailableItems.forEach((obj) => {
        newActiveIds[obj.componentId]._id = obj.newItemId;
      });
      update_items_activeIds_inURL(newActiveIds);
    }

    activeObjs = updateObjViaPaths(activeObjs, mods);
    return activeObjs;
  }

  function unavailableItemAlerts(unavailableItems) {
    unavailableItems?.forEach((item) => {
      const title = item
        .replace(/color/g, "")
        .replace(/(pattern).*$/g, "$1") // remove anything after "pattern"
        .replace(/-/g, " ")
        .replace(/\s+/g, " "); // remove any double spaces
      toastr.info(`"${title}" is unavailable`);
    });
  }

  // traverses activeIds obj and finds any inputs
  // uses those inputs to update item in itemsArray
  // also applies updates from dependencies
  function getUpdatedItemsArray(activeIds) {
    let itemsArrayCopy;
    // prime the itemsState.array if not already present
    if (!itemsState.array) itemsArrayCopy = getApplicableItemsArray();
    // use copy of itemsState.array so any existing inputs or edits are copied
    else itemsArrayCopy = itemsState.array;

    // update items from inputs
    traverse(activeIds, (context) => {
      const { parent, key, value } = context;
      if (key === "inputs" && value) {
        let item = itemsArrayCopy.find((obj) => obj._id === parent._id);
        updateItemWithInputs(item, value);
      }
    });

    // update items from dependencies
    let activeItemIdsArray = getActiveItemIdsArray(activeIds);
    itemsArrayCopy.forEach((item) => {
      updateObjWithDependencies(item, activeItemIdsArray);
    });

    return itemsArrayCopy;
  }

  function getApplicableItemsArray() {
    // make array of all applicable item id's
    let itemIdArray = [];
    componentsState.array.forEach((component) => {
      itemIdArray = itemIdArray.concat(component.items);
    });
    // filter the allItemsArray to only include the applicable ones according to itemIdArray
    let filteredItemsArray = allItemsArray_ref.current.filter(sift({ _id: { $in: itemIdArray } }));

    // if there are non_items, add those to the items array
    if (allNonItemsArray) filteredItemsArray = filteredItemsArray.concat(allNonItemsArray);

    return filteredItemsArray;
  }

  // makes array of activeId's for ease-of-use in updateObjWithDependencies
  function getActiveItemIdsArray(activeItemIds) {
    let activeIdArray = [];
    traverse(activeItemIds, (context) => {
      const { parent, key, value } = context;
      if (key === "_id") activeIdArray.push(value);
    });
    return activeIdArray;
  }

  // updates the item specified with the new inputs
  function updateItemWithInputs(item, newInputObj) {
    // pasting the newInputObj to item.inputs
    item.inputs = newInputObj;
    // traverse new inputs
    Object.entries(newInputObj).forEach(([inputKey, inputValue]) => {
      // traverse item
      traverse(item, (context) => {
        const { parent, key, value, meta } = context;
        // update item values with input values
        if (inputKey == key && !meta.currentPath?.includes("inputs") && inputValue !== null) {
          parent[key] = inputValue;
        }
      });
    });
  }

  // CUSTOM CODE: handles Shopify-specific exclusions
  function applyShopifyExclusions(obj) {
    if (!obj || typeof obj !== "object" || !obj._id) return null; // Return null if no valid object

    // Apply Shopify-specific exclusions
    if (!shopifyData.product["comfort-foam"] && obj._id === "comfort-foam") {
      return true;
    }
    if (!shopifyData.product["tall-seat-cover"] && obj._id === "tall-seat-cover") {
      return true;
    }
    if (!shopifyData.product["double-side-wall"] && obj._id === "double-side-wall") {
      return true;
    }
    if (shopifyData.product["omit-ribs"] === true && (obj._id === "rib-type" || obj._id.includes("ribs-material"))) {
      return true;
    }
    if (
      shopifyData.product["omit-front-patch"] === true &&
      (obj._id === "custom-patch-front" || obj._id === "customText-material-front" || obj._id === "patch-material-front")
    ) {
      return true;
    }
    if (
      shopifyData.product["omit-side-patches"] === true &&
      (obj._id === "custom-patches-side" ||
        obj._id === "customText-material-sides" ||
        obj._id === "patch-material-sides" ||
        obj._id === "side-logo")
    ) {
      return true;
    }
    return false; // Return false if no Shopify exclusion was applied
  }

  function updateObjWithDependencies(obj, activeIdArray) {
    if (!obj?.dependencies || obj.dependencies.length === 0) return;

    // Check if Shopify has excluded this object
    const shopifyExcluded = applyShopifyExclusions(obj);
    if (shopifyExcluded === true) {
      obj.excluded = true;
    }

    // Process all dependencies, but preserve Shopify's excluded value only if it was explicitly set
    obj.dependencies.forEach((dependencyObj) => {
      let isOneItemValueActive = false;
      dependencyObj.itemValues.forEach((objId) => {
        if (activeIdArray?.includes(objId)) {
          isOneItemValueActive = true;
        }
      });

      if (isOneItemValueActive) {
        Object.entries(dependencyObj.updates).forEach(([keyToUpdate, newValue]) => {
          // Only skip excluded updates if Shopify explicitly excluded it
          if (keyToUpdate === "excluded" && shopifyExcluded === true) {
            return;
          }
          obj[keyToUpdate] = newValue;
        });
      } else {
        Object.entries(dependencyObj.updates).forEach(([keyToUpdate, newValue]) => {
          // Only skip excluded updates if Shopify explicitly excluded it
          if (keyToUpdate === "excluded" && shopifyExcluded === true) {
            return;
          }
          obj[keyToUpdate] = !newValue;
        });
      }
    });
  }

  return null;
}
